import { z } from 'zod';
import { TaggedDataSetReference } from 'libs/end-to-end/src/types/databases/data-sets/data-set-reference';
import { AIInterviewerDebugMode } from 'libs/end-to-end/src/types/ai-interviewer/ai-interviewer-debug-mode';
import { ResumeParsingCacheBusterDefaultVersion } from 'libs/end-to-end/src/types/databases/integrations/resume-parsing-cache-buster';
import { AllowedOpenAiModels } from 'libs/end-to-end/src/types/databases/data-sets/data-set-resume-rubric';
import { SelectiveFilterDateBasedOption } from 'libs/end-to-end/src/selective-filtering/selective-filter-matching-option';
import { SelectiveFilterStringBasedOption } from 'libs/end-to-end/src/selective-filtering/selective-filter-matching-option';
import { SecretReference } from 'libs/end-to-end/src/types/secrets/secret-reference';

export const CompositeDataSetCommonModel = z.enum([
  'Employees',
  'EmployeeHistory',
  'Performance',
  'Candidates',
  'Applications',
  'Resumes',
  'EmployeeToCandidateMapping',
]);

export type CompositeDataSetCommonModel = z.infer<
  typeof CompositeDataSetCommonModel
>;

export const CompositeDataSetCommonModels = Object.values(
  CompositeDataSetCommonModel.Values,
);

export const CompositeDataSetReportInDataSet = z.object({
  reportName: z.string(),
  reportPrefixMatcher: z.string(),
});

export type CompositeDataSetReportInDataSet = z.infer<
  typeof CompositeDataSetReportInDataSet
>;

export const CompositeDataSetRecipeType = z.enum([
  'rename-columns',
  'common-model-validation',
  'ai-interviewer',
  'employee-candidate-matching',
  'merge-resume-parsing',
  'resume-address-parser',
  'commute-times',
  'merge-filtering',
  'resume-fetcher',
  'resume-parser',
  'dagster-source',
]);

export type CompositeDataSetRecipeType = z.infer<
  typeof CompositeDataSetRecipeType
>;

export const CompositeDataSetRecipeTypes = Object.values(
  CompositeDataSetRecipeType.Values,
);

export const CompositeDataSetRenameColumnsRecipe = z.object({
  type: z.intersection(z.literal('rename-columns'), CompositeDataSetRecipeType),
  reports: z.array(CompositeDataSetReportInDataSet),
  renamingReportsConfigs: z.array(
    z.object({
      reportName: z.string(),
      columnRenames: z.array(z.object({ from: z.string(), to: z.string() })),
    }),
  ),
});

export type CompositeDataSetRenameColumnsRecipe = z.infer<
  typeof CompositeDataSetRenameColumnsRecipe
>;

export const CompositeDataSetCommonModelValidation = z.object({
  reportName: z.string(),
  commonModelValidator: CompositeDataSetCommonModel,
});

export type CompositeDataSetCommonModelValidation = z.infer<
  typeof CompositeDataSetCommonModelValidation
>;

export const CompositeDataSetCommonModelValidationRecipe = z.object({
  type: z.literal('common-model-validation'),
  reports: z.array(CompositeDataSetReportInDataSet),
  reportsToValidate: z.array(CompositeDataSetCommonModelValidation),
});

export type CompositeDataSetCommonModelValidationRecipe = z.infer<
  typeof CompositeDataSetCommonModelValidationRecipe
>;

export const CompositeDataSetAiInterviewerRubric = z.object({
  filter: SelectiveFilterStringBasedOption,
  rubricConstructId: z.string(),
});

export const CompositeDataSetAiInterviewerRecipe = z.object({
  type: z.intersection(z.literal('ai-interviewer'), CompositeDataSetRecipeType),
  script: z.string(),
  aiInterviewerDebugMode: AIInterviewerDebugMode,
  aiInterviewerStartDate: z.string().optional(),
  openAiModel: AllowedOpenAiModels.default('gpt-4o-mini'),
  rubricsByJobTitle: z.array(CompositeDataSetAiInterviewerRubric).default([]),
  enableHallucinationFilter: z.boolean().optional(),
  combineAnswersInPipeline: z.boolean().default(false), // If there is concern of the order in answers being inconsistent, set this to true so that the answers are combined in the pipeline and grade all provided answers together.
  runOnDagster: z.boolean().default(false),
  runBadLanguageDetector: z.boolean().default(false),
});

export type CompositeDataSetAiInterviewerRecipe = z.infer<
  typeof CompositeDataSetAiInterviewerRecipe
>;

export const CompositeDataSetEmployeeCandidateMatchingRecipeColumnToMatch =
  z.object({
    employeeColumn: z.string(),
    candidateColumn: z.string(),
  });

export type CompositeDataSetEmployeeCandidateMatchingRecipeColumnToMatch =
  z.infer<typeof CompositeDataSetEmployeeCandidateMatchingRecipeColumnToMatch>;

export const CompositeDataSetEmployeeCandidateMatchingRecipe = z.object({
  type: z.intersection(
    z.literal('employee-candidate-matching'),
    CompositeDataSetRecipeType,
  ),
  reports: z.array(CompositeDataSetReportInDataSet),
  idColumn: CompositeDataSetEmployeeCandidateMatchingRecipeColumnToMatch,
  firstNameColumnToMatch:
    CompositeDataSetEmployeeCandidateMatchingRecipeColumnToMatch,
  lastNameColumnToMatch:
    CompositeDataSetEmployeeCandidateMatchingRecipeColumnToMatch,
});

export type CompositeDataSetEmployeeCandidateMatchingRecipe = z.infer<
  typeof CompositeDataSetEmployeeCandidateMatchingRecipe
>;

export const CompositeDataSetMergeResumeParsingRecipeResumeGradingRubric =
  z.object({
    filter: SelectiveFilterStringBasedOption,
    rubricConstructId: z.string(),
  });

export type CompositeDataSetMergeResumeParsingRecipeResumeGradingRubric =
  z.infer<typeof CompositeDataSetMergeResumeParsingRecipeResumeGradingRubric>;

export const CompositeDataSetMergeResumeParsingRecipeResumeGradingConfig =
  z.object({
    openAiModel: AllowedOpenAiModels.default('gpt-4o-mini'),
    rubricsByJobTitle: z
      .array(CompositeDataSetMergeResumeParsingRecipeResumeGradingRubric)
      .nullable()
      .default([]),
  });

export type CompositeDataSetMergeResumeParsingRecipeResumeGradingConfig =
  z.infer<typeof CompositeDataSetMergeResumeParsingRecipeResumeGradingConfig>;

export const CompositeDataSetMergeResumeParsingRecipe = z.object({
  type: z.intersection(
    z.literal('merge-resume-parsing'),
    CompositeDataSetRecipeType,
  ),
  mergeDataSetId: z.string(),
  employeeCandidatesFilterDataSetId: z.string(),
  resumeParsingCacheBusterVersion: z
    .string()
    .default(ResumeParsingCacheBusterDefaultVersion),
  isResumeParsingFilteringDisabled: z.boolean().default(false),
  useV2Grading: z.boolean().default(false),
  triggeredByDagster: z.boolean().default(false),
  gradingConfig:
    CompositeDataSetMergeResumeParsingRecipeResumeGradingConfig.nullable().default(
      null,
    ),
  jobTitleSelectiveFilteringConfigs: SelectiveFilterStringBasedOption.array()
    .nullable()
    .default([]),
  applicationAppliedAtFilteringConfigs: SelectiveFilterDateBasedOption.array()
    .nullable()
    .default([]),
});

export type CompositeDataSetMergeResumeParsingRecipe = z.infer<
  typeof CompositeDataSetMergeResumeParsingRecipe
>;

export const CompositeDataSetResumeAddressParserRecipe = z.object({
  type: z.intersection(
    z.literal('resume-address-parser'),
    CompositeDataSetRecipeType,
  ),
  mergeDataSetId: z.string(),
});

export type CompositeDataSetResumeAddressParserRecipe = z.infer<
  typeof CompositeDataSetResumeAddressParserRecipe
>;

export const CompositeDataSetCommuteTimesRecipe = z.object({
  type: z.intersection(z.literal('commute-times'), CompositeDataSetRecipeType),
  commuteTimesType: z.enum(['Employee', 'Candidate']),
  mergeDataSetId: z.string(),
});

export type CompositeDataSetCommuteTimesRecipe = z.infer<
  typeof CompositeDataSetCommuteTimesRecipe
>;

export const CompositeDataSetMergeFilteringRecipe = z.object({
  type: z.intersection(
    z.literal('merge-filtering'),
    CompositeDataSetRecipeType,
  ),
  mergeDataSetId: z.string(),
  applicationCurrentStageFilteringConfigs: z
    .object({
      jobTitleRule: SelectiveFilterStringBasedOption,
      applicationStageRule: SelectiveFilterStringBasedOption,
    })
    .array(),
});

export type CompositeDataSetMergeFilteringRecipe = z.infer<
  typeof CompositeDataSetMergeFilteringRecipe
>;

export const CompositeDataSetResumeFetcherIntegrationType = z.enum([
  'Workday ATS',
  'Workday HRIS',
]);

export type CompositeDataSetResumeFetcherIntegrationType = z.infer<
  typeof CompositeDataSetResumeFetcherIntegrationType
>;

export const CompositeDataSetResumeFetcherWorkdayCandidatesIntegrationConfig =
  z.object({
    type: z.literal('workday-candidates'),
    integrationType: z.literal('Workday ATS'),
    workdaySoapVersion: z.string(),
    endpoint: z.string(),
    authUsernameSecretReference: SecretReference,
    authPasswordSecretReference: SecretReference,
    fileNamePrefix: z.string(),
    remoteIdColumnIndex: z.number(),
  });

export type CompositeDataSetResumeFetcherWorkdayCandidatesIntegrationConfig =
  z.infer<
    typeof CompositeDataSetResumeFetcherWorkdayCandidatesIntegrationConfig
  >;

export const CompositeDataSetResumeFetcherWorkdayEmployeesIntegrationConfig =
  z.object({
    type: z.literal('workday-employees'),
    integrationType: z.literal('Workday HRIS'),
    workdaySoapVersion: z.string(),
    endpoint: z.string(),
    authUsernameSecretReference: SecretReference,
    authPasswordSecretReference: SecretReference,
    fileNamePrefix: z.string(),
    remoteIdColumnIndex: z.number(),
  });

export type CompositeDataSetResumeFetcherWorkdayEmployeesIntegrationConfig =
  z.infer<
    typeof CompositeDataSetResumeFetcherWorkdayEmployeesIntegrationConfig
  >;

export const CompositeDataSetResumeFetcherIntegrationConfig =
  z.discriminatedUnion('integrationType', [
    CompositeDataSetResumeFetcherWorkdayCandidatesIntegrationConfig,
    CompositeDataSetResumeFetcherWorkdayEmployeesIntegrationConfig,
  ]);

export type CompositeDataSetResumeFetcherIntegrationConfig = z.infer<
  typeof CompositeDataSetResumeFetcherIntegrationConfig
>;

export const CompositeDataSetResumeFetcherRecipe = z.object({
  type: z.intersection(z.literal('resume-fetcher'), CompositeDataSetRecipeType),
  dataSetId: z.string(),
  fileName: z.string(),
  integrationConfig: CompositeDataSetResumeFetcherIntegrationConfig,
});

export type CompositeDataSetResumeFetcherRecipe = z.infer<
  typeof CompositeDataSetResumeFetcherRecipe
>;

export const CompositeDataSetResumeParserRecipeResumeGradingRubric = z.object({
  filter: SelectiveFilterStringBasedOption,
  rubricConstructId: z.string(),
});

export type CompositeDataSetResumeParserRecipeResumeGradingRubric = z.infer<
  typeof CompositeDataSetResumeParserRecipeResumeGradingRubric
>;

export const CompositeDataSetResumeParserRecipeResumeGradingConfig = z.object({
  openAiModel: AllowedOpenAiModels.default('gpt-4o-mini'),
  rubricsByJobTitle: z
    .array(CompositeDataSetResumeParserRecipeResumeGradingRubric)
    .nullable()
    .default([]),
  enableHireDateFiltering: z.boolean().optional(),
});

export type CompositeDataSetResumeParserRecipeResumeGradingConfig = z.infer<
  typeof CompositeDataSetResumeParserRecipeResumeGradingConfig
>;

export const CompositeDataSetResumeParserRecipe = z.object({
  type: z.intersection(z.literal('resume-parser'), CompositeDataSetRecipeType),
  dataSetId: z.string(),
  fileName: z.string(),
  resumeParsingCacheBusterVersion: z
    .string()
    .default(ResumeParsingCacheBusterDefaultVersion),
  useV2Grading: z.boolean().default(false),
  disableDbtJob: z.boolean().optional(),
  writeOutputToS3: z.boolean().default(false),
  gradingConfig:
    CompositeDataSetResumeParserRecipeResumeGradingConfig.nullable().default(
      null,
    ),
});

export type CompositeDataSetResumeParserRecipe = z.infer<
  typeof CompositeDataSetResumeParserRecipe
>;

export const CompositeDataSetDagsterSourceRecipe = z.object({
  type: z.intersection(
    z.literal('dagster-source'),
    CompositeDataSetRecipeType,
  ),
  dagsterTransformId: z.string(),
});

export type CompositeDataSetDagsterSourceRecipe = z.infer<
  typeof CompositeDataSetDagsterSourceRecipe
>;

export const CompositeDataSetRecipe = z.union([
  CompositeDataSetRenameColumnsRecipe,
  CompositeDataSetCommonModelValidationRecipe,
  CompositeDataSetAiInterviewerRecipe,
  CompositeDataSetEmployeeCandidateMatchingRecipe,
  CompositeDataSetMergeResumeParsingRecipe,
  CompositeDataSetResumeAddressParserRecipe,
  CompositeDataSetCommuteTimesRecipe,
  CompositeDataSetMergeFilteringRecipe,
  CompositeDataSetResumeFetcherRecipe,
  CompositeDataSetResumeParserRecipe,
  CompositeDataSetDagsterSourceRecipe,
]);

export type CompositeDataSetRecipe = z.infer<typeof CompositeDataSetRecipe>;

export const CompositeDataSetConfig = z.object({
  dataSetType: z.literal('Composite'),
  taggedDataSetReferences: z.array(z.lazy(() => TaggedDataSetReference)),
  pythonScriptContents: z.string(),
  recipe: CompositeDataSetRecipe.nullable().default(null),
  runOrchestrationOnBatch: z.boolean().default(true),
  runScriptOnBatch: z.boolean().default(true),
});

export type CompositeDataSetConfig = z.infer<typeof CompositeDataSetConfig>;
